import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {Subject, takeUntil} from 'rxjs';
import {PatientModel} from '../../../models/patient/patient.model';
import {PatientService} from '../../../services/patient/patient.service';
import {CdkAccordion, CdkAccordionItem} from '@angular/cdk/accordion';
import {DatePipe, DecimalPipe, NgClass} from '@angular/common';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {MatError, MatInput, MatSuffix} from '@angular/material/input';
import {iResponse, iResponseList} from '../../../models/response/response.model';
import {AuthenticationService} from '../../../services/authentication/authentication.service';
import {ChangePasswordModel} from '../../../models/change-password/change-password.model';
import {InfoMessageService} from '../../../services/infomessage/infomessage.service';
import {MatDatepicker, MatDatepickerInput, MatDatepickerToggle} from '@angular/material/datepicker';
import {DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatOption} from '@angular/material/core';
import {DeviceDetectionService} from '../../../services/device-detection/device-detection.service';
import {MomentDateAdapter} from '@angular/material-moment-adapter';
import {MatSelect} from '@angular/material/select';
import {PharmacyModel} from '../../../models/pharmacy/pharmacy.model';
import {PharmacyService} from '../../../services/pharmacy/pharmacy.service';
import {AddressModel} from '../../../models/registration/registration.model';

export const MY_DATE_FORMATS = {
    parse: {
        dateInput: 'DD.MM.YYYY',
    },
    display: {
        dateInput: 'DD.MM.YYYY',
        monthYearLabel: 'MMM YYYY',
        dateA11yLabel: 'LL',
        monthYearA11yLabel: 'MMMM YYYY',
    },
};

@Component({
    selector: 'app-patient-profile-administration',
    imports: [
        CdkAccordion,
        CdkAccordionItem,
        NgClass,
        ReactiveFormsModule,
        MatInput,
        MatError,
        MatDatepicker,
        MatDatepickerToggle,
        MatSuffix,
        MatDatepickerInput,
        MatSelect,
        MatOption,
    ],
    templateUrl: './patient-profile-administration.component.html',
    styleUrl: './patient-profile-administration.component.sass',
    standalone: true,
})

export class PatientProfileAdministrationComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>()
    @Input() profile: PatientModel | null = null;

    pharmacyList: PharmacyModel[] = [];

    profileForm: FormGroup = new FormGroup({
        email: new FormControl(''),
        firstName: new FormControl(''),
        id: new FormControl(''),
        lastName: new FormControl(''),
        mobileNumber: new FormControl(''),
        phoneNumber: new FormControl(''),
        skype: new FormControl(''),
        title: new FormControl(''),
        dateOfBirth: new FormControl(''),
    })

    deliveryAddressForm: FormGroup = new FormGroup({
        city: new FormControl(''),
        houseNumber: new FormControl(''),
        id: new FormControl(''),
        street: new FormControl(''),
        zipCode: new FormControl(''),
    })

    invoiceAddressForm: FormGroup = new FormGroup({
        city: new FormControl(''),
        houseNumber: new FormControl(''),
        id: new FormControl(''),
        street: new FormControl(''),
        zipCode: new FormControl(''),
    })

    passwordForm: FormGroup = new FormGroup({
        newPassword: new FormControl(''),
        passwordConfirm: new FormControl(''),
    })

    isMobile: boolean = false;

    pharmacyIdFormControl: FormControl = new FormControl('')
    customPharmacyForm = new FormGroup({
        name: new FormControl('', Validators.required),
        street: new FormControl('', Validators.required),
        houseNumber: new FormControl('', Validators.required),
        zipCode: new FormControl('', Validators.required),
        city: new FormControl('', Validators.required),
        phoneNumber: new FormControl('', Validators.required),
    })

    constructor(
        private patientService: PatientService,
        private authenticationService: AuthenticationService,
        private infoMessageService: InfoMessageService,
        private deviceDetectionService: DeviceDetectionService,
        private pharmacyService: PharmacyService,
    ) {
        this.isMobile = deviceDetectionService.getIsMobile();
    }

    ngOnInit() {
        if (this.profile) {
            this.parseProfileToForm();
        }
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    updateProfile(): void {
        if (this.profileForm.invalid && this.deliveryAddressForm.invalid && this.invoiceAddressForm.invalid) {
            return;
        }

        const updatedProfile: PatientModel = {
            ...this.profileForm.value,
            deliveryAddress: this.deliveryAddressForm.value,
            invoiceAddress: this.invoiceAddressForm.value,
        }
        this.update(updatedProfile);
    }

    updatePassword(): void {
        if (this.passwordForm.invalid) {
            return;
        }
        this.passwordUpdate(this.passwordForm.get('newPassword')?.value);
    }

    updatePharmacy(): void {
        if (this.profile) {
            if(this.customPharmacyForm.invalid) {
                this.customPharmacyForm.markAllAsTouched();
                return;
            }
            const pharmacy: PharmacyModel = {
                address: {
                    city: "",
                    houseNumber: "",
                    street: "",
                    zipCode: ""
                },
                email: '',
                id: '',
                isDefault: false,
                name: '',
                noSalesTaxes: false,
                phoneNumber: '',
                serialNumber: 0
            };

            if (this.pharmacyIdFormControl.value == '' || this.pharmacyIdFormControl.value == 'none') {
                this.profile.regularPharmacy = null;
            } else {
                if (this.pharmacyIdFormControl.value === 'custom') {
                    pharmacy.id = '00000000-0000-0000-0000-000000000000';
                    pharmacy.name = <string>this.customPharmacyForm.get('name')?.value;
                    pharmacy.address = <AddressModel>this.customPharmacyForm.value;
                    pharmacy.phoneNumber = <string>this.customPharmacyForm.get('phoneNumber')?.value;
                } else {
                    pharmacy.id = this.pharmacyIdFormControl.value;
                    pharmacy.name = <string>this.customPharmacyForm.get('name')?.value;
                    pharmacy.address = <AddressModel>this.customPharmacyForm.value;
                    pharmacy.phoneNumber = <string>this.customPharmacyForm.get('phoneNumber')?.value;
                }
                this.profile.regularPharmacy = pharmacy;
            }
        }

        this.update(this.profile!);
    }

    checkDisabledState(): void {
        if (this.pharmacyIdFormControl.value !== 'custom') {
            this.customPharmacyForm.disable();
            this.compareRegularPharmacy();
        } else {
            this.customPharmacyForm.reset();
            this.customPharmacyForm.enable();
        }
    }


    private parseProfileToForm(): void {
        this.profileForm.patchValue(this.profile!);
        this.deliveryAddressForm.patchValue(this.profile!.deliveryAddress!);
        this.invoiceAddressForm.patchValue(this.profile!.invoiceAddress!);
        this.getPharmacyList();
    }

    private update(profile: PatientModel): void {
        this.patientService.updateMe(profile)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: iResponse<PatientModel>) => {
                    this.profile = response.data;
                    this.infoMessageService.createMessage('Profildaten erfolgreich aktualisiert', 'success')
                },
                error: (error) => {
                }
            })
    }

    private passwordUpdate(password: string): void {
        const newPassword: ChangePasswordModel = {
            newPassword: password
        }
        this.authenticationService.changePassword(newPassword)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.passwordForm.reset();
                    this.infoMessageService.createMessage('Das Passwort wurde geändert', 'sucess')
                },
                error: (error) => {
                }
            })
    }

    private getPharmacyList() {
        this.pharmacyService.getPharmacies()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: iResponseList<PharmacyModel>) => {
                    this.pharmacyList = response.data;

                    if (this.profile?.regularPharmacy && this.profile.regularPharmacy!.id) {
                        if (this.profile?.regularPharmacy.id === '00000000-0000-0000-0000-000000000000') {
                            this.pharmacyIdFormControl.patchValue('custom');
                            this.customPharmacyForm.patchValue(this.profile!.regularPharmacy!.address)
                            this.customPharmacyForm.get('name')?.patchValue(this.profile!.regularPharmacy!.name);
                            this.customPharmacyForm.get('phoneNumber')?.patchValue(this.profile!.regularPharmacy!.phoneNumber);

                        } else {
                            this.pharmacyIdFormControl.patchValue(this.profile!.regularPharmacy!.id);
                            this.compareRegularPharmacy();
                        }
                    }

                },
                error: (error) => {
                }
            })
    }

    private compareRegularPharmacy(): void {
        const found = this.pharmacyList.find(pharmacy => pharmacy.id === this.pharmacyIdFormControl.value);
        if (found) {
            this.customPharmacyForm.patchValue(found.address);
            this.customPharmacyForm.get('name')?.patchValue(found.name);
            this.customPharmacyForm.disable();
        }
    }

}
